import { useEffect, useRef, useState } from 'react';

import { toneFor } from '../lib/status.js';
import { maskDayMonthYear, toDayMonthYear, toIsoKey } from '../lib/dates.js';
import { subscribeToToasts } from '../lib/toast.js';

/**
 * The small set of shapes this panel repeats: badge, button, field, card,
 * table, dialog and the three states every data view needs.
 */

export function Badge({ status, label, tone, children }) {
  const resolved = tone || toneFor(status);
  const text = children ?? label ?? (status ? String(status).replace(/_/g, ' ') : '');
  return <span className={`badge badge--${resolved}`}>{text}</span>;
}

export function Button({
  children,
  variant = 'secondary',
  size,
  block,
  loading,
  disabled,
  type = 'button',
  ...rest
}) {
  return (
    <button
      type={type}
      className={[
        'button',
        `button--${variant}`,
        size ? `button--${size}` : '',
        block ? 'button--block' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : children}
    </button>
  );
}

export function Field({ label, hint, error, children, id }) {
  return (
    <div className="field">
      {label ? (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      ) : null}
      {children}
      {error ? (
        <span className="field__error" role="alert">
          {error}
        </span>
      ) : hint ? (
        <span className="field__hint">{hint}</span>
      ) : null}
    </div>
  );
}

export function Input({ error, ...rest }) {
  return (
    <input className={`field__control ${error ? 'field__control--error' : ''}`} {...rest} />
  );
}

// Drawn rather than pulled from an icon package: two glyphs do not justify a
// dependency. `currentColor` keeps them on the button's own colour.
function EyeIcon() {
  return (
    <svg className="password__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.06 12.35a1 1 0 0 1 0-.7 10.75 10.75 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-19.88 0" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg className="password__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.73 5.08a10.75 10.75 0 0 1 11.21 6.57 1 1 0 0 1 0 .7 10.75 10.75 0 0 1-1.45 2.49" />
      <path d="M14.08 14.16a3 3 0 0 1-4.24-4.24" />
      <path d="M17.48 17.5a10.75 10.75 0 0 1-15.42-5.15 1 1 0 0 1 0-.7 10.75 10.75 0 0 1 4.45-5.14" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

// Hiding a password by default is the right guard against someone reading over
// your shoulder; revealing it on demand is how you catch a typo.
export function PasswordInput({ error, id, ...rest }) {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="password">
      <Input id={id} type={isVisible ? 'text' : 'password'} error={error} {...rest} />
      <button
        type="button"
        className="password__toggle"
        onClick={() => setIsVisible((visible) => !visible)}
        aria-controls={id}
        aria-pressed={isVisible}
        aria-label={isVisible ? 'Hide password' : 'Show password'}
        title={isVisible ? 'Hide password' : 'Show password'}
      >
        {isVisible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

/**
 * A dd/mm/yyyy date field with the browser's own calendar behind it.
 *
 * The visible control is a masked text input, so the format is ours rather
 * than the browser locale's. The native `<input type="date">` is kept in the
 * DOM, sized to nothing, purely so `showPicker()` has something to open and
 * anchor to — a picker is the fastest way to enter a date you have to think
 * about, and typing is the fastest way to enter one you already know.
 */
export function DateInput({ value, onChange, error, id, ...rest }) {
  const nativeRef = useRef(null);

  const openPicker = () => {
    const native = nativeRef.current;
    if (!native) return;

    // Older browsers have no showPicker; focusing at least reaches the control.
    if (typeof native.showPicker === 'function') native.showPicker();
    else native.focus();
  };

  return (
    <div className="date-field">
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(maskDayMonthYear(event.target.value))}
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        error={error}
        {...rest}
      />

      <button
        type="button"
        className="date-field__picker"
        onClick={openPicker}
        aria-label="Choose a date from the calendar"
        title="Choose a date"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4" />
        </svg>
      </button>

      <input
        ref={nativeRef}
        type="date"
        className="date-field__native"
        value={toIsoKey(value)}
        onChange={(event) => onChange(toDayMonthYear(event.target.value))}
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}

export function Select({ children, ...rest }) {
  return (
    <select className="field__control" {...rest}>
      {children}
    </select>
  );
}

export function Textarea({ error, ...rest }) {
  return (
    <textarea className={`field__control ${error ? 'field__control--error' : ''}`} {...rest} />
  );
}

export function Card({ title, subtitle, actions, children, bodyless }) {
  return (
    <section className="card">
      {title || actions ? (
        <header className="card__header">
          <div>
            <h3 className="card__title">{title}</h3>
            {subtitle ? <p className="card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      {bodyless ? children : <div className="card__body">{children}</div>}
    </section>
  );
}

const TREND_ICON = {
  up: 'm4 14 6-6 4 4 6-6',
  down: 'm4 10 6 6 4-4 6 6',
};

/**
 * A headline figure.
 *
 * `icon` and `delta` are optional so the plain form still works; when given,
 * the card takes the Stitch dashboard shape — icon chip left, change pill
 * right, label and value beneath. `progress` (0-100) draws the bar the
 * occupancy card uses.
 */
export function Metric({ label, value, caption, accent, icon, delta, progress }) {
  const direction = delta == null ? null : delta < 0 ? 'down' : 'up';

  return (
    <div className={`metric ${accent ? `metric--${accent}` : ''}`}>
      {icon || direction ? (
        <div className="metric__head">
          {icon ? <span className="metric__icon">{icon}</span> : <span />}

          {direction ? (
            <span className={`metric__delta metric__delta--${direction}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d={TREND_ICON[direction]} />
                <path d={direction === 'up' ? 'M15 8h5v5' : 'M15 16h5v-5'} />
              </svg>
              {Math.abs(delta)}%
            </span>
          ) : null}
        </div>
      ) : null}

      <span className="metric__label">{label}</span>
      <span className="metric__value">{value}</span>
      {caption ? <span className="metric__caption">{caption}</span> : null}

      {progress == null ? null : (
        <div className="metric__progress">
          <div style={{ width: `${Math.max(0, Math.min(100, progress))}%` }} />
        </div>
      )}
    </div>
  );
}

/**
 * A tab strip.
 *
 * The selected tab is owned by the screen rather than by this component, so a
 * page can keep it in the URL and have a refresh or a shared link land on the
 * same view.
 */
export function Tabs({ tabs, value, onChange, label }) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => {
        const isActive = tab.value === value;

        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            className={`tabs__tab ${isActive ? 'tabs__tab--active' : ''}`}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
            {tab.count == null ? null : <span className="tabs__count">{tab.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- States ---------------- */

export function Loading({ label = 'Loading' }) {
  return (
    <div className="state" role="status">
      <span className="spinner" aria-hidden="true" />
      <span className="state__body">{label}…</span>
    </div>
  );
}

/**
 * The API never returns internals, so `error.message` is safe to show. A
 * request id is included when there is one — it is what makes a user-reported
 * failure traceable to a single log line.
 */
export function ErrorState({ error, onRetry, title = 'Could not load this' }) {
  return (
    <div className="state state--error" role="alert">
      <span className="state__title">{title}</span>
      <span className="state__body">{error?.message || 'Something went wrong.'}</span>
      {error?.requestId ? <span className="mono muted">Request {error.requestId}</span> : null}
      {onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, body, action }) {
  return (
    <div className="state">
      <span className="state__title">{title}</span>
      {body ? <span className="state__body">{body}</span> : null}
      {action}
    </div>
  );
}

export function TableSkeleton({ rows = 6, columns = 5 }) {
  return (
    <tbody>
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }, (_, columnIndex) => (
            <td key={columnIndex}>
              <div className="skeleton" style={{ height: 14, width: columnIndex === 0 ? '70%' : '45%' }} />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/* ---------------- Table ---------------- */

/**
 * A table with its own loading, error and empty states, so every list view
 * gets all three without repeating them.
 */
export function DataTable({
  columns,
  rows,
  query,
  empty,
  onRetry,
  rowKey = (row) => row._id,
  onRowClick,
}) {
  const isLoading = query?.isLoading;
  const isError = query?.isError;

  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={column.width ? { width: column.width } : undefined}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        {isLoading ? (
          <TableSkeleton columns={columns.length} />
        ) : (
          <tbody>
            {isError ? (
              <tr>
                <td colSpan={columns.length}>
                  <ErrorState error={query.error} onRetry={onRetry || query.refetch} />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState title={empty?.title || 'Nothing here yet'} body={empty?.body} />
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className={onRowClick ? 'table__row--clickable' : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key} className={column.className}>
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        )}
      </table>
    </div>
  );
}

export function Pagination({ meta, page, onPageChange }) {
  if (!meta || !meta.total) return null;

  const from = (page - 1) * (meta.pageSize || 20) + 1;
  const to = Math.min(page * (meta.pageSize || 20), meta.total);

  return (
    <div className="pagination">
      <span>
        {from}–{to} of {meta.total.toLocaleString()}
      </span>
      <div className="pagination__controls">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={!meta.hasMore}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

/* ---------------- Dialog ---------------- */

/**
 * Modal dialog. Closes on Escape and returns focus behaviour to the browser —
 * back-office work is keyboard-heavy and a trap here would be painful.
 */
export function Dialog({ open, onClose, title, children, footer, warning, size = 'default' }) {
  const backdropRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="dialog-backdrop"
      ref={backdropRef}
      onMouseDown={(event) => {
        if (event.target === backdropRef.current) onClose?.();
      }}
      role="presentation"
    >
      <div className={`dialog dialog--${size}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="dialog__header">
          <h3>{title}</h3>
        </header>
        <div className="dialog__body">
          {warning ? <div className="dialog__warning">{warning}</div> : null}
          {children}
        </div>
        {footer ? <footer className="dialog__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

/* ---------------- Toasts ---------------- */

/**
 * Renders whatever `toast()` dispatches. The store itself lives in
 * `lib/toast.js`, so a non-component module can raise one.
 */
export function ToastHost() {
  const [items, setItems] = useState([]);

  useEffect(
    () =>
      subscribeToToasts((entry) => {
        setItems((current) => [...current, entry]);
        setTimeout(() => {
          setItems((current) => current.filter((item) => item.id !== entry.id));
        }, 4200);
      }),
    [],
  );

  if (!items.length) return null;

  return (
    <div className="toast-host">
      {items.map((item) => (
        <div key={item.id} className={`toast toast--${item.tone}`} role="status">
          {item.message}
        </div>
      ))}
    </div>
  );
}

export function DefinitionList({ items }) {
  return (
    <dl className="definition-list">
      {items
        .filter((item) => item.value != null && item.value !== '')
        .map((item) => (
          <div key={item.label} style={{ display: 'contents' }}>
            <dt>{item.label}</dt>
            <dd>{item.value}</dd>
          </div>
        ))}
    </dl>
  );
}

export function BarChart({ series, valueKey = 'value' }) {
  if (!series?.length) return <EmptyState title="No data for this period" />;

  const max = Math.max(...series.map((point) => point[valueKey] || 0), 1);

  return (
    <div className="bar-chart">
      {series.map((point, index) => (
        <div className="bar-chart__column" key={point.label || index} title={`${point.label}: ${point[valueKey]}`}>
          <div
            className={`bar-chart__bar ${index === series.length - 1 ? 'bar-chart__bar--active' : ''}`}
            style={{ height: `${Math.max(4, ((point[valueKey] || 0) / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  );
}
